// The preview: a secured file opened in place, so the drive stays the thing you read from rather
// than a place you download out of.
//
// Documents open as paper on the sunken ground, because a page is judged against the room it is
// read in; media plays on the one dark ground in the drive. Either way the header keeps Download,
// so reading something does not mean losing what you could do with it, and arrows at the edges —
// or the left and right keys — step through the other secured files beside it. A media player
// takes the arrow keys for itself, to skip.
//
// Each file is opened from a decrypted copy the API prepares on request (see "useDownloads"), which
// takes a few seconds, so opening is a state of its own — for media, the same ring the player turns
// while it buffers, so decrypting and buffering read as one wait. Media streams straight from the copy, which
// is kept on while the player is in use; if the player loses it anyway, it picks up where it was
// from another url. A player left paused, or never started, for ten minutes is taken to have been
// abandoned, and its copy is left to expire. It isn't taken away at once, since another tab may be
// playing from the same copy; playing again keeps it on again, or finds another once it has gone.
// Everything else is read into memory first: the copy is stored as an attachment, which a frame
// pointed at it would save rather than show. A PDF is drawn page by page (see "PdfPages") rather
// than left to a browser that may have no viewer for it. A kind the drive cannot read inside, or text too long
// to lay out, gets a real state that says so and offers the download instead of an empty frame.
//
// Where the file can cross between the drive and Locked, the header offers that too, as the row's
// menu does: out of Locked for a file opened there, into it for one opened in the drive. The drive
// asks before moving it, and the preview closes once the file has left the listing it was opened from.
//
// A photo, video or recording can open its details beside the stage (see "DetailsSheet"), from the
// header or with the I key. They stay open as the preview steps from file to file, and are only
// asked for while they are, since reading them opens the person's account key on the API's side.
//
// A picture can be zoomed into and dragged around (see "useZoom"), with the controls in the header
// and the plus, minus and zero keys. The left and right keys still step to the next file while it
// is zoomed; a step lands on the next picture fitted.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useEffect, useRef, useState} from 'react'
import {useQuery} from '@apollo/client/react'

import CloseIcon from '@mui/icons-material/CloseRounded'
import Dialog from '@mui/material/Dialog'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import FailedIcon from '@mui/icons-material/ErrorOutlineRounded'
import FileIcon from '@mui/icons-material/InsertDriveFileOutlined'
import IconButton from '@mui/material/IconButton'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import LockIcon from '@mui/icons-material/LockOutlined'
import NextIcon from '@mui/icons-material/ChevronRightRounded'
import PreviousIcon from '@mui/icons-material/ChevronLeftRounded'
import SealOutIcon from '@mui/icons-material/LockOpenOutlined'
import Tooltip from '@mui/material/Tooltip'
import UnlockIcon from '@mui/icons-material/LockOpenRounded'
import ZoomInIcon from '@mui/icons-material/ZoomInRounded'
import ZoomOutIcon from '@mui/icons-material/ZoomOutRounded'

import DetailsSheet from './DetailsSheet'
import MediaPlayer, {PlayerWait, isTyping} from './MediaPlayer'
import PdfPages from './PdfPages'
import {BaseButton} from '../shared/blocks/Buttons'
import {KIND_ICONS} from './DriveItems'

import {UPLOAD_DETAILS} from '../../graphql/queries/uploads'
import {fileExtension, fileKind, formatBytes, formatTimestamp, previewKind} from '../../lib/fileTools'
import {readFile} from '../../lib/hooks/useDownloads'
import useZoom, {formatZoom} from '../../lib/hooks/useZoom'

import './filePreview.scss'

const MEDIA = ['video', 'audio']
// The kinds that keep tags about how, when and where they were made.
const DESCRIBED = ['image', ...MEDIA]

// Beyond this, laying the text out would stall the tab for longer than it takes to download it.
const TEXT_LIMIT = 2 * 1024 * 1024

// How often a copy being played from is checked on, so it is kept on before it nears its expiry.
const KEEP_EVERY = 60 * 1000
const ABANDONED_AFTER = 10 * 60 * 1000

const PICTURE_ZOOM = 8

const ZOOM_KEYS = {'+': 'zoomIn', '=': 'zoomIn', '-': 'zoomOut', '0': 'reset'}

const fileShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  filesize: PropTypes.number,
  fileType: PropTypes.string,
  updatedAt: PropTypes.string.isRequired
})

// What "useDownloads" offers the preview: finding a file's copy, keeping it on while it plays, and
// finding another when the player loses it.
const downloadsShape = PropTypes.shape({
  locate: PropTypes.func.isRequired,
  keep: PropTypes.func.isRequired,
  relocate: PropTypes.func.isRequired
})

// What "useZoom" hands the picture's surface to wear.
const zoomShape = PropTypes.shape({
  ref: PropTypes.func.isRequired,
  style: PropTypes.object,
  handlers: PropTypes.object.isRequired,
  zoomed: PropTypes.bool.isRequired,
  panning: PropTypes.bool.isRequired
})

const pictureBox = surface => {
  const picture = surface.querySelector('img')

  return {left: picture.offsetLeft, top: picture.offsetTop, width: picture.offsetWidth, height: picture.offsetHeight}
}

const useDetails = (file, asked) => {
  const {data, error} = useQuery(UPLOAD_DETAILS, {variables: {id: file.id}, skip: !asked})
  const details = data?.upload?.details ?? null

  if (error) return {details, status: 'failed'}

  return {details, status: data ? 'ready' : 'loading'}
}

const tooLong = (file, kind) => ['text', 'code'].includes(kind) && file.filesize > TEXT_LIMIT

// Turns a prepared copy into what the body draws: a url to stream or show, a PDF's bytes, or the
// text itself.
// A url made here belongs to the preview, and is handed back for it to revoke.
const load = async (kind, url, file) => {
  if (MEDIA.includes(kind)) return {url}

  const blob = await readFile(url, file)

  if (kind === 'text' || kind === 'code') return {text: await blob.text()}
  if (kind === 'pdf') return {bytes: new Uint8Array(await blob.arrayBuffer())}

  return {url: URL.createObjectURL(blob), owned: true}
}

const useSource = (file, kind, {locate, keep, relocate}, attempt) => {
  const [source, setSource] = useState({status: 'opening'})
  const opened = useRef(null)
  const pausedSince = useRef(null)
  const streaming = MEDIA.includes(kind) && source.status === 'ready'

  useEffect(() => {
    if (!kind || tooLong(file, kind)) return

    let current = true
    let owned = null

    opened.current = file.id

    setSource({status: 'opening'})
    locate(file)
      .then(url => load(kind, url, file))
      .then(loaded => {
        if (loaded.owned) owned = loaded.url
        if (!current) return owned && URL.revokeObjectURL(owned)

        return setSource({status: 'ready', ...loaded})
      })
      .catch(() => current && setSource({status: 'failed'}))

    return () => {
      current = false
      if (owned) URL.revokeObjectURL(owned)
    }
  }, [file.id, attempt])

  const keepOn = () => keep(file).catch(() => {})
  const abandoned = () => pausedSince.current !== null && Date.now() - pausedSince.current >= ABANDONED_AFTER

  useEffect(() => {
    if (!streaming) return

    pausedSince.current = Date.now()
    keepOn()

    const keeping = setInterval(() => abandoned() || keepOn(), KEEP_EVERY)

    return () => clearInterval(keeping)
  }, [file.id, streaming])

  const recover = () => {
    const recovering = file.id

    relocate(file, source.url)
      .then(url => opened.current === recovering && setSource({status: 'ready', url}))
      .catch(() => opened.current === recovering && setSource({status: 'failed'}))
  }

  const followPlaying = playing => {
    pausedSince.current = playing ? null : Date.now()
    if (playing) keepOn()
  }

  return {...source, recover, followPlaying}
}

const Notice = ({Icon, title, children, action}) => (
  <div className='preview-notice'>
    <Icon className='notice-glyph' />
    <div className='notice-title'>{title}</div>
    <div className='notice-body'>{children}</div>
    {action}
  </div>
)

Notice.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  action: PropTypes.node
}

const DownloadInstead = ({file, onDownload}) => (
  <BaseButton className='notice-action' onClick={() => onDownload(file)}><DownloadIcon fontSize='small' />Download</BaseButton>
)

DownloadInstead.propTypes = {file: fileShape.isRequired, onDownload: PropTypes.func.isRequired}

const CodePage = ({text}) => {
  const lines = text.replace(/\s+$/, '').split('\n')

  return (
    <div className='preview-code'>
      <div className='code-numbers' aria-hidden='true'>{lines.map((line, index) => <span key={index}>{index + 1}</span>)}</div>
      <pre className='code-source'>{lines.join('\n')}</pre>
    </div>
  )
}

CodePage.propTypes = {text: PropTypes.string.isRequired}

const PreviewBody = ({file, kind, downloads, zoom, filling, onFill, onNotice, onDownload}) => {
  const [attempt, setAttempt] = useState(0)
  const [unreadable, setUnreadable] = useState(false)
  const [pdfFailure, setPdfFailure] = useState(null)
  const source = useSource(file, kind, downloads, attempt)
  const extension = fileExtension(file.filename).toUpperCase() || 'these'

  if (!kind) {
    return (
      <Notice Icon={FileIcon} title='No preview for this kind of file' action={<DownloadInstead file={file} onDownload={onDownload} />}>
        The drive can hold {extension} files but cannot read inside them. Download it to open in whatever does.
      </Notice>
    )
  }

  if (tooLong(file, kind)) {
    return (
      <Notice Icon={FileIcon} title='Too long to read here' action={<DownloadInstead file={file} onDownload={onDownload} />}>
        At {formatBytes(file.filesize)} this is more text than the reader lays out. Download it to open in whatever does.
      </Notice>
    )
  }

  if (source.status === 'opening' && MEDIA.includes(kind))
    return <PlayerWait label='Decrypting' detail={formatBytes(file.filesize)} Icon={UnlockIcon} />

  if (source.status === 'opening') {
    return (
      <div className='preview-opening' role='status'>
        <span className='opening-spinner' />
        Decrypting a copy to read…
      </div>
    )
  }

  if (source.status === 'failed') {
    return (
      <Notice
        Icon={FailedIcon}
        title='This file couldn’t be opened'
        action={<BaseButton className='notice-action' onClick={() => setAttempt(attempt + 1)}>Try again</BaseButton>}>
        The drive couldn’t get a readable copy of it just now.
      </Notice>
    )
  }

  if (MEDIA.includes(kind))
    return <MediaPlayer file={file} src={source.url} video={kind === 'video'} filling={filling} onFill={onFill} onNotice={onNotice} onLost={source.recover} onPlayingChange={source.followPlaying} />

  if (kind === 'code') return <CodePage text={source.text} />

  if (kind === 'text') return <article className='preview-paper'><pre className='paper-text'>{source.text}</pre></article>

  if (pdfFailure === 'locked') {
    return (
      <Notice Icon={LockIcon} title='This PDF is password-protected' action={<DownloadInstead file={file} onDownload={onDownload} />}>
        The reader can’t open a PDF that asks for a password. Download it to open in whatever can.
      </Notice>
    )
  }

  if (pdfFailure) {
    return (
      <Notice Icon={FileIcon} title='This PDF can’t be shown here' action={<DownloadInstead file={file} onDownload={onDownload} />}>
        Its pages couldn’t be laid out. Download it to open in whatever does.
      </Notice>
    )
  }

  if (kind === 'pdf') return <PdfPages bytes={source.bytes} title={file.filename} onFailed={setPdfFailure} />

  // Kept apart from a failed copy: the copy came, but this browser cannot draw its format.
  if (unreadable) {
    return (
      <Notice Icon={FileIcon} title='This image can’t be shown here' action={<DownloadInstead file={file} onDownload={onDownload} />}>
        This browser cannot draw {extension} images. Download it to open in whatever does.
      </Notice>
    )
  }

  return (
    <div className='preview-image'>
      <div
        ref={zoom.ref}
        className={classnames('picture-surface', {zoomed: zoom.zoomed, panning: zoom.panning})}
        style={zoom.style}
        {...zoom.handlers}>
        <img src={source.url} alt={file.filename} draggable={false} onError={() => setUnreadable(true)} />
      </div>
    </div>
  )
}

PreviewBody.propTypes = {
  file: fileShape.isRequired,
  kind: PropTypes.string,
  downloads: downloadsShape.isRequired,
  zoom: zoomShape.isRequired,
  filling: PropTypes.bool.isRequired,
  onFill: PropTypes.func.isRequired,
  onNotice: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired
}

const FilePreview = ({file, siblings, downloads, sealed = false, onOpen, onClose, onDownload, onSeal = null, onNotice}) => {
  const [filling, setFilling] = useState(false)
  const [showingDetails, setShowingDetails] = useState(false)
  const kind = previewKind(file.filename)
  const media = MEDIA.includes(kind)
  const described = DESCRIBED.includes(kind)
  const detailing = described && showingDetails
  const {details, status} = useDetails(file, detailing)
  const zoom = useZoom({most: PICTURE_ZOOM, contentBox: pictureBox})
  const fitted = media || kind === 'image'
  const Icon = KIND_ICONS[fileKind(file.filename)]
  const at = siblings.findIndex(({id}) => id === file.id)
  const previous = at > 0 ? siblings[at - 1] : null
  const next = at > -1 ? siblings[at + 1] : null
  const meta = `${formatBytes(file.filesize)} · ${formatTimestamp(file.updatedAt)}`

  const step = target => {
    setFilling(false)
    onOpen(target)
  }

  // Escape brings a player that is filling the window back into the preview before it closes it.
  const handleClose = () => (filling ? setFilling(false) : onClose())

  const toggleDetails = () => setShowingDetails(!showingDetails)

  const handleKey = event => {
    const plain = !event.metaKey && !event.ctrlKey && !event.altKey && !isTyping(event.target)

    if (described && plain && event.key.toLowerCase() === 'i') {
      event.preventDefault()
      toggleDetails()
      return
    }

    if (zoom.ready && plain && ZOOM_KEYS[event.key]) {
      event.preventDefault()
      zoom[ZOOM_KEYS[event.key]]()
      return
    }

    if (media || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return

    const target = event.key === 'ArrowLeft' ? previous : next

    if (target) step(target)
  }

  return (
    <Dialog
      open
      fullScreen
      onClose={handleClose}
      onKeyDown={handleKey}
      aria-labelledby='preview-name'
      slotProps={{paper: {className: classnames('file-preview', {media, sealed})}}}>
      <header className='preview-bar'>
        <Icon className='preview-glyph' />
        <span className='preview-names'>
          <span className='preview-name' id='preview-name'>{file.filename}</span>
          <span className='preview-meta'>{meta}</span>
        </span>
        {/* The copy on screen outlives nothing: it closes when Locked reseals. On a phone the icon
            stands alone to leave room for Close, so what it means opens on a tap rather than a long
            press; wider, it is spelled out and the tooltip stays hidden. */}
        {sealed && (
          <Tooltip
            title='Decrypted in this tab only'
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            slotProps={{popper: {className: 'preview-seal-tip'}}}>
            <span className='preview-seal' tabIndex={0}>
              <LockIcon fontSize='small' /><span className='preview-seal-label'>Decrypted in this tab only</span>
            </span>
          </Tooltip>
        )}
        {zoom.ready && (
          <span className='preview-zoom'>
            <IconButton className='preview-button' aria-label='Zoom out' title='Zoom out (−)' disabled={!zoom.zoomed} onClick={zoom.zoomOut}>
              <ZoomOutIcon />
            </IconButton>
            <button className='zoom-level' type='button' title='Fit to screen (0)' onClick={zoom.reset}>{formatZoom(zoom.scale)}</button>
            <IconButton className='preview-button' aria-label='Zoom in' title='Zoom in (+)' disabled={zoom.largest} onClick={zoom.zoomIn}>
              <ZoomInIcon />
            </IconButton>
          </span>
        )}
        {described && (
          <IconButton
            className={detailing ? 'preview-button on' : 'preview-button'}
            aria-label='Details'
            aria-pressed={detailing}
            title='Details (I)'
            onClick={toggleDetails}>
            <InfoIcon />
          </IconButton>
        )}
        <IconButton className='preview-button' aria-label='Download' title='Download' onClick={() => onDownload(file)}><DownloadIcon /></IconButton>
        {onSeal && (
          <IconButton
            className='preview-button'
            aria-label={sealed ? 'Move out of Locked' : 'Move to Locked'}
            title={sealed ? 'Move out of Locked' : 'Move to Locked'}
            onClick={() => onSeal(file)}>
            {sealed ? <SealOutIcon /> : <LockIcon />}
          </IconButton>
        )}
        <IconButton className='preview-button' aria-label='Close preview' title='Close' onClick={onClose}><CloseIcon /></IconButton>
      </header>

      <div className='preview-body'>
        <div className={classnames('preview-stage', {fitted, picture: kind === 'image'})}>
          {previous && (
            <IconButton className='preview-step previous' aria-label='Previous file' onClick={() => step(previous)}><PreviousIcon /></IconButton>
          )}
          <PreviewBody
            key={file.id}
            file={file}
            kind={kind}
            downloads={downloads}
            zoom={zoom}
            filling={filling}
            onFill={setFilling}
            onNotice={onNotice}
            onDownload={onDownload}
          />
          {next && <IconButton className='preview-step next' aria-label='Next file' onClick={() => step(next)}><NextIcon /></IconButton>}
        </div>
        {detailing && (
          <DetailsSheet file={file} kind={kind} details={details} status={status} onClose={toggleDetails} onNotice={onNotice} />
        )}
      </div>

      {!media && (
        <footer className='preview-foot'>
          <span>{meta}</span>
          <span className='foot-spacer' />
          <span>{fileExtension(file.filename).toUpperCase()}</span>
        </footer>
      )}
    </Dialog>
  )
}

FilePreview.propTypes = {
  file: fileShape.isRequired,
  // The secured files beside this one, in the order the listing shows them.
  siblings: PropTypes.arrayOf(fileShape).isRequired,
  downloads: downloadsShape.isRequired,
  // A file in Locked, opened while it is.
  sealed: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  // Where the file can cross between the drive and Locked: handed the file, to move it out of
  // Locked when "sealed", and into it otherwise.
  onSeal: PropTypes.func,
  onNotice: PropTypes.func.isRequired
}

export default FilePreview
